/* mx4ever rider size engine – phase 1 prototype.
 * Pure functions, no DOM: the same file will run in the theme.
 *
 * rider:   { group: 'adult'|'youth'|'women', chest, bust, waist, hip, thigh, inside_leg, arm, height, head, eu, foot }
 *          (all cm; eu = EU shoe size; foot = foot length, optional)
 * table:   one structured size table (from convert.py)
 * product: { table, use, sizes: { "<size as sold>": ["<chart row>", ...] } }
 *
 * Result:  { fit, size, alt, ideal, basis, message }
 *   fit = 'good'        rider's size is clear and the product has it
 *         'between'     rider is between two sizes; size = recommended, alt = the other one
 *         'not_offered' rider's size (ideal) exists in the chart but this product isn't sold in it
 *         'no_size'     rider is bigger/smaller than the whole chart (direction)
 *         'missing'     the rider profile lacks the measurement needed
 *         'unsized'     product has no chart rows
 */
(function (root) {
  // How much each measurement counts, per category. The first one the rider has is the main one.
  const WEIGHTS = {
    jersey:         { chest: 3, waist: 1, height: 0.5, arm: 0.3 },
    women_jersey:   { bust: 3, chest: 2, waist: 1, height: 0.5, arm: 0.3 },
    trouser:        { waist: 3, hip: 1, inside_leg: 1, thigh: 0.5, height: 0.3 },
    casual_top:     { chest: 3, waist: 1 },
    casual_trouser: { waist: 3, hip: 1 },
    youth_jersey:   { chest: 3, height: 1, waist: 0.5, arm: 0.3 },
    youth_trouser:  { waist: 3, inside_leg: 1, height: 1, hip: 0.5 },
    helmet:         { head: 1 },
    hat:            { head: 1 },
  };
  // How far (cm) outside a size's range still counts as that size.
  const TOLERANCE = { helmet: 0.5, hat: 1, default: 2 };
  const LABEL = { chest: 'chest', bust: 'bust', waist: 'waist', head: 'head', eu: 'shoe size', foot: 'foot length' };

  function profileFor(table, product) {
    if (table.category === 'mx_clothing') return product && product.use === 'trouser' ? 'youth_trouser' : 'youth_jersey';
    if (table.category === 'jersey' && table.group === 'women') return 'women_jersey';
    return table.category;
  }

  function dev(range, v) {                       // 0 inside the range, else cm outside it
    if (v == null || !range) return null;
    if (v < range[0]) return range[0] - v;
    if (v > range[1]) return v - range[1];
    return 0;
  }

  // chart row -> size name as sold on this product
  function soldAs(product) {
    const map = new Map();
    for (const [sold, rows] of Object.entries(product.sizes || {})) {
      if (rows) for (const r of rows) if (!map.has(r)) map.set(r, sold);
    }
    return map;
  }

  // ---- Boots: EU size (or foot length when we have it and the chart lists it) ----
  function idealBoot(rider, table) {
    const useFoot = rider.foot != null && table.rows.every(r => r.m.foot);
    if (!useFoot && rider.eu == null) return { fit: 'missing', missing: ['eu'] };
    const rows = table.rows.slice().sort((a, b) => a.m.eu[0] - b.m.eu[0]);
    if (useFoot) {
      // Brand rule (Alpinestars): the right boot is the one where comfort size <= foot <= inside length.
      const v = rider.foot;
      const hits = rows.filter(r => v >= r.m.foot[0] && v <= r.m.foot[1]);
      if (hits.length) return { fit: 'good', row: hits[hits.length - 1], basis: 'foot' };   // on a border: the larger boot
      const up = rows.find(r => r.m.foot[0] > v);
      if (!up) return { fit: 'no_size', direction: 'larger', basis: 'foot' };
      if (up === rows[0] && up.m.foot[0] - v > 1) return { fit: 'no_size', direction: 'smaller', basis: 'foot' };
      return { fit: 'between', row: up, basis: 'foot' };
    }
    const v = rider.eu;
    const pick = rows.find(r => r.m.eu[0] >= v - 0.25);                // smallest boot that is big enough
    if (!pick) return { fit: 'no_size', direction: 'larger', basis: 'eu' };
    if (pick.m.eu[0] - v > 1.5) return { fit: 'no_size', direction: 'smaller', basis: 'eu' };
    const exact = Math.abs(pick.m.eu[0] - v) < 0.01;
    return { fit: exact ? 'good' : 'between', row: pick, basis: 'eu',
             note: exact ? null : `EU ${v} isn't made in this boot – EU ${pick.size} is the next size up` };
  }

  // ---- Clothing / helmets: score every chart row ----
  function idealMeasured(rider, table, product) {
    const profile = profileFor(table, product);
    const weights = Object.assign({}, WEIGHTS[profile] || {});
    const primary = Object.keys(weights).find(k => rider[k] != null && table.rows.some(r => r.m[k]));
    if (!primary) return { fit: 'missing', missing: [Object.keys(weights)[0]] };
    const tol = TOLERANCE[table.category] != null ? TOLERANCE[table.category] : TOLERANCE.default;
    const snug = table.category === 'helmet' || table.category === 'hat';   // helmets: smaller when in doubt

    const scored = table.rows.map((row, i) => {
      let score = 0; const d = {};
      for (const [k, w] of Object.entries(weights)) {
        const x = dev(row.m[k], rider[k]);
        if (x == null) continue;
        d[k] = x; score += (k === primary ? 10 * w : w) * x;
      }
      return { row, i, score, p: d[primary] };
    }).filter(s => s.p != null);

    const minP = Math.min(...scored.map(s => s.p));
    if (minP > tol) {
      const v = rider[primary];
      const top = Math.max(...scored.map(s => s.row.m[primary][1]));
      return { fit: 'no_size', direction: v > top ? 'larger' : 'smaller', basis: primary };
    }
    // Candidates: rows that fit the main measurement best (border/overlap/gap give 2)
    let cands;
    if (minP === 0) cands = scored.filter(s => s.p === 0);
    else {                                         // in a gap between two sizes, or just outside the end
      const best = scored.find(s => s.p === minP);
      cands = scored.filter(s => s.p <= tol && Math.abs(s.i - best.i) <= 1);
    }
    cands.sort((a, b) => a.i - b.i);
    if (cands.length === 1) return { fit: minP === 0 ? 'good' : 'between', row: cands[0].row, basis: primary };
    const low = Math.min(...cands.map(c => c.score));
    const level = cands.filter(c => c.score - low < 0.01);
    if (level.length === 1) {                      // other measurements decide
      const other = cands.find(c => c !== level[0]);
      return { fit: minP === 0 ? 'good' : 'between', row: level[0].row, altRow: minP === 0 ? null : other.row, basis: primary };
    }
    const pick = snug ? level[0] : level[level.length - 1];
    const alt = snug ? level[1] : level[level.length - 2];
    return { fit: 'between', row: pick.row, altRow: alt.row, basis: primary };
  }

  function recommend(rider, table, product) {
    const sold = soldAs(product);
    if (!sold.size) return finish({ fit: 'unsized' });
    const ideal = table.category === 'boot' ? idealBoot(rider, table) : idealMeasured(rider, table, product);
    if (ideal.fit === 'no_size' && table.group === 'youth' && ideal.direction === 'larger') ideal.outgrown = true;
    if (!ideal.row) return finish(ideal);
    const out = Object.assign({}, ideal, { ideal: ideal.row.size });
    if (sold.has(ideal.row.size)) {
      out.size = sold.get(ideal.row.size);
      out.alt = ideal.altRow && sold.has(ideal.altRow.size) ? sold.get(ideal.altRow.size) : null;
    } else if (ideal.altRow && sold.has(ideal.altRow.size)) {
      out.size = sold.get(ideal.altRow.size);     // between sizes and only the other one is sold
      out.fit = 'between';
    } else {
      out.fit = 'not_offered';
    }
    return finish(out);
  }

  function finish(r) {
    const res = { fit: r.fit, size: r.size || null, alt: r.alt || null, ideal: r.ideal || null,
                  basis: r.basis || null, direction: r.direction || null, missing: r.missing || null };
    const what = LABEL[res.basis] || res.basis;
    res.message =
      res.fit === 'good'        ? (r.note || `Your size: ${res.size}`) :
      res.fit === 'between'     ? (r.note || (res.alt ? `Between sizes (${what}): ${res.size} recommended, ${res.alt} also possible`
                                                      : `Between sizes (${what}): ${res.size} is the closest`)) :
      res.fit === 'not_offered' ? `Your size is ${res.ideal} – this product isn't available in that size` :
      res.fit === 'no_size'     ? (r.outgrown ? 'Too big for youth sizes – look at the adult version'
                                              : `This chart doesn't go ${res.direction === 'larger' ? 'big' : 'small'} enough for this rider`) :
      res.fit === 'missing'     ? `Add ${res.missing.map(m => LABEL[m] || m).join(', ')} to the rider to see a size` :
                                  'No size information for this product';
    return res;
  }

  const api = { recommend };
  if (typeof module !== 'undefined') module.exports = api; else root.MxSizeEngine = api;
})(this);
/* mx4ever rider profile: saved riders (browser), garage strip, rider form, product size box.
   Riders live in localStorage 'mx:riders' next to the bike garage ('ymm:garage'). */
(() => {
  if (window.MxRiders) return;
  const KEY = 'mx:riders';
  const MAX = 8;
  const FIELDS = ['chest', 'bust', 'waist', 'hip', 'inside_leg', 'height', 'head', 'eu', 'foot'];

  const T = (() => {
    try {
      const el = document.getElementById('mx-rider-strings');
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  })();
  const t = (key, vars) => {
    let s = T[key] || key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(v);
    return s;
  };
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const store = {
    read() {
      try {
        const v = JSON.parse(localStorage.getItem(KEY));
        if (v && Array.isArray(v.riders)) return v;
      } catch (e) {}
      return { current: null, riders: [] };
    },
    write(v) {
      try {
        localStorage.setItem(KEY, JSON.stringify(v));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('mx-rider:change', { detail: v }));
    },
    current() {
      const v = this.read();
      return v.riders.find((r) => r.id === v.current) || null;
    },
    get(id) {
      return this.read().riders.find((r) => r.id === id) || null;
    },
    save(rider) {
      const v = this.read();
      rider.id = rider.id || `r${Date.now().toString(36)}`;
      rider.updated = new Date().toISOString().slice(0, 10);
      v.riders = [rider, ...v.riders.filter((r) => r.id !== rider.id)].slice(0, MAX);
      v.current = rider.id;
      this.write(v);
      return rider;
    },
    select(id) {
      const v = this.read();
      v.current = id;
      this.write(v);
    },
    remove(id) {
      const v = this.read();
      v.riders = v.riders.filter((r) => r.id !== id);
      if (v.current === id) v.current = v.riders[0] ? v.riders[0].id : null;
      this.write(v);
    },
  };

  // possessive for Danish strings: "Emils", "Lars'", "Alpinestars'"
  const gen = (x) => (/[sxz]$/i.test(String(x)) ? `${x}'` : `${x}s`);
  const groupLabel = (g) => t(`group_${g || 'adult'}`);
  const riderLine = (r) => (r ? `${r.name} · ${groupLabel(r.group)}` : '');
  const measuredAgo = (r) => {
    if (!r || !r.updated) return '';
    const [y, m, d] = String(r.updated).split('-').map(Number);
    const today = new Date();
    const days = Math.round((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - new Date(y, m - 1, d)) / 864e5);
    if (!(days >= 1)) return t('measured_today');
    if (days === 1) return t('measured_yesterday');
    if (days < 45) return t('measured_days', { n: days });
    return t('measured_months', { n: Math.round(days / 30) });
  };
  const openForm = (id) => document.dispatchEvent(new CustomEvent('mx-rider:edit', { detail: { id: id || null } }));

  /* ---------- Rider form (one <dialog> per page, rendered by the garage strip section) ---------- */
  class MxRiderForm extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.groupSel = this.form.elements.group;
      this.groupSel.addEventListener('change', () => this.syncGroup());
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.dialog.close()));
      this.removeBtn = this.querySelector('[data-remove]');
      this.removeBtn.addEventListener('click', () => {
        if (this.editing) store.remove(this.editing);
        this.dialog.close();
      });
      document.addEventListener('mx-rider:edit', (e) => this.open(e.detail && e.detail.id));
    }
    syncGroup() {
      const g = this.groupSel.value;
      this.querySelectorAll('[data-only]').forEach((el) => {
        el.hidden = !el.dataset.only.split(' ').includes(g);
      });
    }
    open(id) {
      const r = id ? store.get(id) : null;
      this.editing = r ? r.id : null;
      this.form.reset();
      this.querySelector('[data-title]').textContent = r ? t('form_edit_title', { name: r.name }) : t('form_add_title');
      this.removeBtn.hidden = !r;
      if (r) {
        this.form.elements.name.value = r.name || '';
        this.groupSel.value = r.group || 'adult';
        for (const f of FIELDS) if (this.form.elements[f] && r[f] != null) this.form.elements[f].value = r[f];
      }
      this.syncGroup();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.form.elements.name.focus();
    }
    onSubmit(e) {
      e.preventDefault();
      const el = this.form.elements;
      const name = el.name.value.trim();
      if (!name) {
        el.name.focus();
        return;
      }
      const rider = { id: this.editing, name, group: el.group.value };
      for (const f of FIELDS) {
        const input = el[f];
        if (!input || input.closest('[hidden]')) continue;
        const n = parseFloat(String(input.value).replace(',', '.'));
        if (!isNaN(n) && n > 0) rider[f] = n;
      }
      store.save(rider);
      this.dialog.close();
    }
  }

  /* ---------- Garage strip: current bike + current rider ---------- */
  class MxGarageBar extends HTMLElement {
    connectedCallback() {
      this.riderPill = this.querySelector('[data-rider-pill]');
      this.riderMenu = this.querySelector('[data-rider-menu]');
      this.bikeLabel = this.querySelector('[data-bike-label]');
      this.bikeShop = this.querySelector('[data-bike-shop]');
      this.renderRider = this.renderRider.bind(this);
      this.renderBike = this.renderBike.bind(this);
      document.addEventListener('mx-rider:change', this.renderRider);
      document.addEventListener('ymm:change', this.renderBike);
      this.renderRider();
      // ymm.js is deferred; render the bike once it has loaded
      if (window.YMM) this.renderBike();
      else window.addEventListener('DOMContentLoaded', this.renderBike);
      // close the dropdowns when clicking elsewhere
      document.addEventListener('click', (e) => {
        this.querySelectorAll('details[open]').forEach((d) => {
          if (!d.contains(e.target)) d.open = false;
        });
      });
    }
    renderBike() {
      const g = window.YMM && window.YMM.garage;
      const cur = g ? g.current() : null;
      let label = '';
      if (cur) {
        const sel = this.querySelector('ymm-selector');
        const pre = sel ? sel.dataset.preLabel : 'Pre __YEAR__';
        const y = String(cur.parts[0]);
        label = [y.startsWith('pre') ? pre.replace('__YEAR__', y.slice(3)) : y, cur.parts[1], cur.parts[2]].join(' ');
      }
      this.bikeLabel.textContent = label || t('add_bike');
      this.dataset.hasBike = cur ? 'true' : 'false';
      if (this.bikeShop) {
        this.bikeShop.hidden = !cur;
        if (cur) this.bikeShop.href = `${this.dataset.partsUrl.replace(/\/+$/, '')}/${cur.tag}?sort_by=best-selling`;
      }
    }
    renderRider() {
      const v = store.read();
      const cur = store.current();
      this.riderPill.innerHTML = cur
        ? `<strong>${esc(cur.name)}</strong> <span class="mx-garage__sub">${esc(groupLabel(cur.group))}</span>`
        : esc(t('add_rider'));
      this.dataset.hasRider = cur ? 'true' : 'false';
      const items = v.riders
        .map(
          (r) => `<li class="mx-garage__row${r.id === v.current ? ' is-current' : ''}">
            <button type="button" class="mx-garage__choose" data-pick="${esc(r.id)}"${r.id === v.current ? ' aria-current="true"' : ''}>${esc(riderLine(r))}</button>
            <button type="button" class="link underlined-link mx-garage__edit" data-edit="${esc(r.id)}">${esc(t('edit'))}</button>
          </li>`
        )
        .join('');
      this.riderMenu.innerHTML = `${items ? `<ul class="mx-garage__list" role="list">${items}</ul>` : `<p class="mx-garage__empty">${esc(t('rider_intro'))}</p>`}
        <button type="button" class="button button--secondary mx-garage__add" data-add>${esc(t('add_rider'))}</button>`;
      this.riderMenu.querySelectorAll('[data-pick]').forEach((b) =>
        b.addEventListener('click', () => {
          b.closest('details').open = false; // before select(): it re-renders this menu
          store.select(b.dataset.pick);
        })
      );
      this.riderMenu.querySelectorAll('[data-edit]').forEach((b) =>
        b.addEventListener('click', () => {
          b.closest('details').open = false;
          openForm(b.dataset.edit);
        })
      );
      this.riderMenu.querySelector('[data-add]').addEventListener('click', (e) => {
        e.target.closest('details').open = false;
        openForm(null);
      });
    }
  }

  /* ---------- Product page: the rider's size ---------- */
  const CATEGORY_WORD = { jersey: 'word_jersey', trouser: 'word_trouser', casual_top: 'word_top', mx_clothing: 'word_kit', boot: 'word_boot', helmet: 'word_helmet', hat: 'word_hat' };

  class MxRiderSize extends HTMLElement {
    connectedCallback() {
      // Rendered by the garage strip section; move under the main product's variant picker
      if (!this.closest('product-info')) {
        const picker = document.querySelector('product-info variant-selects');
        if (picker) {
          picker.after(this);
          return; // moving re-runs connectedCallback in the new place
        }
        this.hidden = true;
        return;
      }
      if (this._ready) return;
      this._ready = true;
      this.hidden = false;
      this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.box = this.querySelector('[data-box]');
      this.autoPicked = false;
      this.render = this.render.bind(this);
      document.addEventListener('mx-rider:change', () => {
        this.autoPicked = false;
        this.render();
      });
      // Dawn replaces the variant picker when an option changes: put the size tag back.
      // Watch product-info (the picker element itself is swapped out); tagSize() is a no-op when nothing changed.
      this.observer = new MutationObserver(() => this.tagSize());
      this.observer.observe(this.closest('product-info'), { childList: true, subtree: true });
      this.render();
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
    table() {
      const d = this.data;
      return { id: d.handle, group: d.group, category: d.category, rows: d.rows };
    }
    product() {
      const sizes = {};
      const rows = this.data.rows;
      const norm = (s) => {
        s = String(s).trim().toUpperCase().replace(/\s+/g, '');
        return { '2XL': 'XXL', XXXL: '3XL', '2XS': 'XXS', XXXS: '3XS' }[s] || s;
      };
      for (const v of this.data.values) {
        const parts = String(v).split('/');
        const hit = parts.map((p) => {
          const r = rows.find((row) => [row.size, ...Object.values(row.aliases || {})].some((n) => norm(n) === norm(p)));
          return r ? r.size : null;
        });
        sizes[v] = hit.every(Boolean) ? hit : null;
      }
      return { sizes, use: this.data.use };
    }
    render() {
      const rider = store.current();
      this.result = null;
      if (!rider) return this.show('none', { title: t('none_title'), text: t('none_text') }, { action: 'add' });

      const d = this.data;
      const word = t(CATEGORY_WORD[d.category] || 'word_item');
      const youthProduct = d.group === 'youth';
      const youthRider = rider.group === 'youth';
      const sizedByBody = !['helmet', 'boot', 'hat'].includes(d.category);
      if (sizedByBody && youthRider && !youthProduct)
        return this.show('warn', { title: t('adult_item', { word, name: rider.name }), text: t('adult_item_text', { name: rider.name }) });
      if (sizedByBody && !youthRider && youthProduct)
        return this.show('warn', { title: t('youth_item', { word }), text: t('youth_item_text', { name: rider.name }) });
      if (sizedByBody && d.group === 'women' && rider.group !== 'women')
        return this.show('warn', { title: t('women_item', { word }), text: t('women_item_text', { name: rider.name }) });

      const r = window.MxSizeEngine.recommend(rider, this.table(), this.product());
      this.result = r;
      const name = rider.name;
      switch (r.fit) {
        case 'good':
          this.show('fits', { title: t('fits_title', { size: r.size, name, name_gen: gen(name) }), text: r.basis === 'eu' && r.note ? r.note : this.why(rider, r) });
          break;
        case 'between':
          this.show('fits', {
            title: t('fits_title', { size: r.size, name, name_gen: gen(name) }),
            text: r.basis === 'eu' ? t('next_size_up', { eu: rider.eu, size: r.size }) : r.alt ? t('between_text', { size: r.size, alt: r.alt }) : t('between_closest'),
          });
          break;
        case 'not_offered':
          this.show('nofit', { title: t('not_offered_title', { size: r.ideal, name }), text: t('not_offered_text') });
          break;
        case 'no_size':
          this.show('nofit', {
            title: t(r.direction === 'larger' ? 'too_small_title' : 'too_big_title', { name }),
            text: youthProduct && r.direction === 'larger' ? t('outgrown_text') : t('no_size_text'),
          });
          break;
        case 'missing':
          this.show('none', { title: t('missing_title', { name }), text: t('missing_text', { what: (r.missing || []).map((m) => t(`m_${m}`)).join(', ') }) }, { action: 'edit', id: rider.id });
          break;
        default:
          this.show('none', { title: t('no_data_title'), text: '' });
      }
      this.tagSize();
      this.autoSelect();
    }
    why(rider, r) {
      const parts = [];
      const add = (k) => {
        if (rider[k] != null) parts.push(t('why_part', { what: t(`m_${k}`), cm: rider[k] }));
      };
      if (r.basis === 'foot') add('foot');
      else if (r.basis === 'head') add('head');
      else {
        add(r.basis);
        if (r.basis === 'chest' || r.basis === 'bust') add('waist');
        if (r.basis === 'waist') add('inside_leg');
      }
      const brand = this.data.brand;
      const brand_gen = gen(brand);
      return parts.length ? t('why', { brand, brand_gen, list: parts.join(t('and')), size: r.ideal || r.size }) : t('why_short', { brand, brand_gen });
    }
    show(state, msg, opts = {}) {
      const rider = store.current();
      const icon = { fits: '&#10003;', nofit: '&#10005;', warn: '!', none: '?' }[state];
      this.dataset.result = state;
      let link = '';
      if (opts.action === 'add') link = `<button type="button" class="link underlined-link mx-size__action" data-add>${esc(t('add_rider'))}</button>`;
      if (opts.action === 'edit') link = `<button type="button" class="link underlined-link mx-size__action" data-edit="${esc(opts.id)}">${esc(t('add_measurements'))}</button>`;
      this.box.innerHTML = `<div class="ymm-fitment__msg mx-size__msg mx-size__msg--${state}">
          <span class="ymm-fitment__icon" aria-hidden="true">${icon}</span>
          <p><strong>${esc(msg.title)}</strong>${msg.text ? `<br><span class="mx-size__text">${esc(msg.text)}</span>` : ''}${
            state === 'fits' ? `<span class="mx-size__stock" data-stock hidden><br>${esc(t('sold_out', { size: this.result ? this.result.size : '' }))}</span>` : ''
          }${link ? `<br>${link}` : ''}</p>
        </div>
        ${
          rider
            ? `<p class="mx-size__meta"><button type="button" class="link underlined-link" data-switch>${esc(t('change_rider'))}</button><span>${esc(measuredAgo(rider))}</span></p>`
            : ''
        }`;
      const add = this.box.querySelector('[data-add]');
      if (add) add.addEventListener('click', () => openForm(null));
      const edit = this.box.querySelector('[data-edit]');
      if (edit) edit.addEventListener('click', () => openForm(edit.dataset.edit));
      const sw = this.box.querySelector('[data-switch]');
      if (sw)
        sw.addEventListener('click', () => {
          const bar = document.querySelector('mx-garage-bar details[data-rider-details]');
          if (bar) {
            bar.open = true;
            bar.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const first = bar.querySelector('button');
            if (first) first.focus();
          } else openForm(rider.id);
        });
      if (state !== 'fits') this.tagSize();
    }
    inputs() {
      const info = this.closest('product-info') || document;
      // Dawn's input names can carry whitespace/newlines ("Size-1\n"): compare without it
      const want = String(this.data.inputName).replace(/\s+/g, '');
      return [...info.querySelectorAll('variant-selects input[type="radio"]')].filter((i) => i.name.replace(/\s+/g, '') === want);
    }
    // Dawn shows an option as buttons (radios) or, per product, as a dropdown: <select name="options[Size]">
    sizeSelect() {
      const info = this.closest('product-info') || document;
      const name = String(this.data.inputName).replace(/-\d+\s*$/, '').trim();
      return info.querySelector(`variant-selects select[name="options[${CSS.escape(name)}]"]`);
    }
    // dropdown version of the size tag: "44.5 · Simon" on the rider's option (+ sold-out note)
    tagOption(select, want, rider) {
      let target = null;
      for (const o of select.options) {
        if (want && o.value === want) {
          target = o;
          continue;
        }
        if (o.dataset.mxOrig != null) {
          o.textContent = o.dataset.mxOrig;
          delete o.dataset.mxOrig;
        }
      }
      const stock = this.box && this.box.querySelector('[data-stock]');
      if (stock) {
        const orig = target ? (target.dataset.mxOrig != null ? target.dataset.mxOrig : target.textContent) : '';
        const out = !!(target && (target.disabled || orig.trim() !== target.value));
        if (stock.hidden === out) stock.hidden = !out;
      }
      if (!target) return;
      if (target.dataset.mxOrig == null) target.dataset.mxOrig = target.textContent;
      const text = `${target.dataset.mxOrig.trim()} · ${rider.name}`;
      if (target.textContent !== text) target.textContent = text;
    }
    tagSize() {
      const r = this.result;
      const rider = store.current();
      const want = r && (r.fit === 'good' || r.fit === 'between') ? r.size : null;
      const info = this.closest('product-info') || document;
      const select = this.sizeSelect();
      if (select) return this.tagOption(select, want && rider ? want : null, rider);
      const input = want && rider ? this.inputs().find((i) => i.value === want) : null;
      const label = input && info.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      // the rider's size sold out in the chosen colour (Dawn marks it with class "disabled")
      const stock = this.box && this.box.querySelector('[data-stock]');
      if (stock) {
        const out = !!(input && input.classList.contains('disabled'));
        if (stock.hidden === out) stock.hidden = !out;
      }
      const tags = [...info.querySelectorAll('.mx-size-tag')];
      // already right: leave the DOM alone (our own change would wake the observer again)
      if (label && tags.length === 1 && tags[0].parentNode === label && tags[0].textContent === rider.name) return;
      if (!label && !tags.length) return;
      tags.forEach((el) => el.remove());
      if (label) {
        const tag = document.createElement('span');
        tag.className = 'mx-size-tag';
        tag.textContent = rider.name;
        label.append(tag);
      }
    }
    autoSelect() {
      const r = this.result;
      if (this.autoPicked || !r || (r.fit !== 'good' && r.fit !== 'between')) return;
      this.autoPicked = true;
      if (new URL(location.href).searchParams.has('variant')) return; // shopper chose a variant via link
      const select = this.sizeSelect();
      if (select) {
        if (select.value !== r.size && [...select.options].some((o) => o.value === r.size)) {
          select.value = r.size;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      const input = this.inputs().find((i) => i.value === r.size);
      if (input && !input.checked) input.click();
    }
  }

  window.MxRiders = { store, openForm };
  customElements.define('mx-rider-form', MxRiderForm);
  customElements.define('mx-garage-bar', MxGarageBar);
  customElements.define('mx-rider-size', MxRiderSize);
})();
